import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../common';
import reportsV2Service from '../../services/reportsV2Service';
import './ReportList.css';

const ReportList = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingPDF, setGeneratingPDF] = useState(null);

  const CUTOFF_DATE = new Date('2026-04-22T05:00:00Z');
  const isLegacyReport = (report) => new Date(report.createdAt) < CUTOFF_DATE;

  useEffect(() => { loadReports(); }, []);

  const loadReports = async () => {
    setLoading(true);
    try {
      const response = await reportsV2Service.getReports();
      const reports = response.data.reports || [];
      setReports(reports.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
    } catch (error) {
      console.error('Failed to load reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePDF = async (reportId) => {
    setGeneratingPDF(reportId);
    try {
      await reportsV2Service.generatePDF(reportId);
      alert('PDF 생성이 시작되었습니다. 완료되면 다운로드 버튼이 표시됩니다.');
      await loadReports();
    } catch (error) {
      alert('PDF 생성에 실패했습니다: ' + error.message);
    } finally {
      setGeneratingPDF(null);
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm('정말로 이 보고서를 삭제하시겠습니까?')) return;
    try {
      await reportsV2Service.deleteReport(reportId);
      setReports(prev => prev.filter(r => r.reportId !== reportId));
    } catch (error) {
      alert('삭제에 실패했습니다: ' + error.message);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'PENDING':     { text: '대기중', class: 'status-pending' },
      'IN_PROGRESS': { text: '진행중', class: 'status-progress' },
      'COMPLETED':   { text: '완료',   class: 'status-completed' },
      'FAILED':      { text: '실패',   class: 'status-failed' }
    };
    const s = statusMap[status] || { text: status, class: 'status-unknown' };
    return <span className={`status-badge ${s.class}`}>{s.text}</span>;
  };

  const formatDate = (d) => new Date(d).toLocaleString('ko-KR');

  const getStepStatusText = (status) => ({
    'PENDING': '대기중', 'IN_PROGRESS': '진행중', 'COMPLETED': '완료', 'SKIPPED': '생략'
  }[status] || status);

  return (
    <div className="report-list-container">
      <div className="report-list-header">
        <div className="header-left">
          <h2>보고서 목록</h2>
          <span className="report-count">{reports.length}개의 보고서</span>
        </div>
        <div className="header-actions">
          <Button variant="secondary" size="sm" onClick={loadReports} disabled={loading}>
            {loading ? '로딩 중...' : '새로고침'}
          </Button>
          <Button variant="primary" size="md" onClick={() => window.location.href = '/report/generate'}>
            + 새 보고서 생성
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="report-list-loading">로딩 중...</div>
      ) : reports.length === 0 ? (
        <div className="report-list-empty">생성된 보고서가 없습니다.</div>
      ) : (
        <div className="report-list">
          {reports.map((report) => (
            <div key={report.reportId} className="report-item">
              <div className="report-item__header">
                <h3>{report.metadata?.authorName || '보고서'}</h3>
                <div className="report-item__actions">
                  {getStatusBadge(report.status)}
                  {report.status === 'COMPLETED' && !isLegacyReport(report) && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleGeneratePDF(report.reportId)}
                      disabled={generatingPDF === report.reportId || report.pdfStatus?.status === 'IN_PROGRESS'}
                    >
                      {generatingPDF === report.reportId || report.pdfStatus?.status === 'IN_PROGRESS' ? 'PDF 생성 중...' : 'PDF 생성'}
                    </Button>
                  )}
                  {report.pdfStatus?.status === 'COMPLETED' && report.pdfStatus?.url && (
                    <Button variant="primary" size="sm" onClick={() => window.open(report.pdfStatus.url, '_blank')}>
                      PDF 다운로드
                    </Button>
                  )}
                  {!isLegacyReport(report) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/report/edit/${report.reportId}`)}
                    >
                      편집
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteReport(report.reportId)} className="delete-btn">
                    삭제
                  </Button>
                </div>
              </div>
              <div className="report-item__details">
                <p><strong>생성일:</strong> {formatDate(report.createdAt)}</p>
                <p><strong>기간:</strong> {report.metadata?.reportStartDate} ~ {report.metadata?.reportEndDate}</p>
                <p><strong>Role ARN:</strong> {report.metadata?.roleArn}</p>
              </div>
              {isLegacyReport(report) && (
                <div className="legacy-notice">
                  2026년 4월 22일 14시 이전에 생성된 보고서는 PDF 생성 및 편집이 지원되지 않습니다. 필요시 새 보고서를 생성해 주세요.
                </div>
              )}
              {report.progress && (
                <div className="report-item__steps">
                  <h4>진행 상태:</h4>
                  <div className="steps-grid">
                    {report.config?.collectServiceInfo && (
                      <div className="step-item">
                        <span className="step-label">리소스 수집:</span>
                        <span className={`step-status status-${report.progress.resourceCollection?.toLowerCase()}`}>
                          {getStepStatusText(report.progress.resourceCollection)}
                        </span>
                      </div>
                    )}
                    {report.config?.collectMetrics && (
                      <div className="step-item">
                        <span className="step-label">메트릭 수집:</span>
                        <span className={`step-status status-${report.progress.metricCollection?.toLowerCase()}`}>
                          {getStepStatusText(report.progress.metricCollection)}
                        </span>
                      </div>
                    )}
                    {report.config?.addInspectionItems && (
                      <div className="step-item">
                        <span className="step-label">리소스 검사:</span>
                        <span className={`step-status status-${report.progress.resourceInspection?.toLowerCase()}`}>
                          {getStepStatusText(report.progress.resourceInspection)}
                        </span>
                      </div>
                    )}
                    {report.config?.addInspectionItems && report.config?.enableAIInspection && (
                      <div className="step-item">
                        <span className="step-label">AI 분석:</span>
                        <span className={`step-status status-${report.progress.aiAnalysis?.toLowerCase()}`}>
                          {getStepStatusText(report.progress.aiAnalysis)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReportList;
